/* iceberg-ui */
import { Box, PieChart, PieChartEntry } from '@auviknetworks/iceberg-ui';

/*  Shared  */
import { popoverContentsColumns } from 'application/Shared/Components/Recharts/PopoverContents';
import { Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import { TFunction } from 'i18next';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/*  Components  */
import { DataVisualCard } from './DataVisualCard';

interface GetPieDataProps {
    accessorKey?: string;
    data: unknown[];
    nameMap: Record<string, unknown>;
    t: TFunction<'translation', undefined>;
}

/**
 * Transforms raw data into a format suitable for Rechart pie charts
 * @param accessorKey - Optional key to access nested data properties
 * @param data - Array of data objects to transform
 * @param nameMap - Mapping of data keys to display names
 * @param t - Translation function
 * @returns Array of PieChartEntry objects with name and value properties
 */
const getPieData = ({ accessorKey, data, nameMap, t }: GetPieDataProps) => {
    // If it has an accessorKey it means we still need to extract the data, otherwise the data comes formatted, one hopes
    let rechartPieData: PieChartEntry[] = accessorKey
        ? []
        : (data as PieChartEntry[]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pieData: Record<string, any> = {};

    if (accessorKey) {
        Object.values(data).forEach((row) => {
            if (!row) {
                return;
            }
            try {
                // If the accessorKey is of the type 'application.risk', then split will return ["application", "risk"]
                // and the reduce will return application[risk].
                // If the accessorKey is of the type 'status' and it does not have a separator, then the split will return ["status"]
                // and the reduce will return just the status value of the row.
                const subAccesorKey = accessorKey
                    .split('.')
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .reduce((a: any, b: string) => a?.[b], row);

                // If there is no subAccesorKey, don't do anything
                if (subAccesorKey == null) {
                    return;
                }

                // If the pieData does not have this key, add it
                if (!pieData[subAccesorKey]) {
                    pieData[subAccesorKey] = 1;
                } else {
                    // If it did have it, then increment by one.
                    pieData[subAccesorKey] += 1;
                }
            } catch (e) {
                debug.e('Unable to transform pie data', e);
                Notify.e(t('Common.error'));
            }
        });

        // At this point, for something like risk, pieData looks like { "Low": 54, "Medium": 69, "Critical": 38, "Safe": 1, "High": 34 }
        // We need to transform it to an array of objects, like
        // [{ name: "Low", value: 54 }, { name: "Medium", value: 69 }, { name: "Critical", value: 38 }, { name: "Safe", value: 1 }, { name: "High", value: 34 }]
        Object.keys(pieData).forEach((key: string) => {
            // We don't need to show entries with zero value
            if (!pieData[key] || pieData[key] === 0) {
                return;
            }
            rechartPieData.push({
                name:
                    !!nameMap && nameMap[key] ? (nameMap[key] as string) : key,
                value: pieData[key]
            });
        });
    } else {
        // We don't need to show entries with zero value
        rechartPieData = rechartPieData.filter((item) => !(item.value === 0));
    }

    return rechartPieData;
};

interface TransformPieDataProps {
    accessorKey?: string;
    data: unknown[];
    maxNumberOfPieSlicesToDisplay: number;
    nameMap: Record<string, unknown>;
    setTransforming: React.Dispatch<React.SetStateAction<boolean>>;
    t: TFunction<'translation', undefined>;
}

/**
 * Transforms raw data into pie chart format with a maximum number of slices
 * @param accessorKey - Optional key to access nested data properties
 * @param data - Array of data objects to transform
 * @param maxNumberOfPieSlicesToDisplay - Maximum number of pie slices to show before grouping remaining into "Other"
 * @param nameMap - Mapping of data keys to display names
 * @param setTransforming - State setter function to track transformation status
 * @param t - Translation function
 * @returns Array of PieChartEntry objects sorted by value with at most maxNumberOfPieSlicesToDisplay + 1 entries
 */
const transformPieData = ({
    accessorKey,
    data,
    maxNumberOfPieSlicesToDisplay,
    nameMap,
    setTransforming,
    t
}: TransformPieDataProps) => {
    setTransforming(true);

    if (!data?.length) {
        setTransforming(false);
        return [];
    }

    let pieData = getPieData({ accessorKey, data, nameMap, t });

    // Sort descending
    pieData.sort((a, b) => {
        if (a.value > b.value) return -1;
        if (a.value < b.value) return 1;
        return 0;
    });

    // We also show only N (#maxNumberOfPieSlicesToDisplay) results, which defaults to the top 5,
    // but we add a "Other" category with the sum of the rest of the entries
    if (pieData.length > maxNumberOfPieSlicesToDisplay) {
        let x = 0;
        let otherCount = 0;
        Object.values(pieData).forEach((entry) => {
            if (!entry) {
                return;
            }

            x += 1;
            if (x > maxNumberOfPieSlicesToDisplay) {
                otherCount += entry.value;
            }
        });
        pieData = pieData.slice(0, maxNumberOfPieSlicesToDisplay);
        pieData.push({
            name: t('Common.other'),
            value: otherCount
        });
    }
    setTransforming(false);
    return pieData;
};

interface DataAPIVisualsPieChartProps {
    accessorKey?: string;
    card?: boolean;
    cursor?: string;
    data: unknown[];
    emptyMessage?: string;
    filteredResults?: boolean;
    isRiskChart?: boolean;
    loading?: boolean;
    maxNumberOfPieSlicesToDisplay?: number;
    nameMap: Record<string, unknown>;
    noLegend?: boolean;
    showPopover?: boolean;
    subtitle?: string;
    title: string;
    tooltip?: string;
}

export const DataAPIVisualsPieChart = ({
    accessorKey,
    card,
    cursor,
    data,
    emptyMessage,
    filteredResults,
    isRiskChart = false,
    loading,
    maxNumberOfPieSlicesToDisplay = 5,
    nameMap,
    noLegend,
    showPopover = true,
    subtitle,
    title,
    tooltip
}: DataAPIVisualsPieChartProps) => {
    const { t } = useTranslation();
    const [transforming, setTransforming] = useState(true); // We are getting data ready, show a loading element.

    const [pieData, setPieData] = useState<PieChartEntry[]>([]);

    useEffect(() => {
        const transformedPieData = transformPieData({
            accessorKey,
            data,
            maxNumberOfPieSlicesToDisplay,
            nameMap,
            setTransforming,
            t
        });

        setPieData(transformedPieData);
    }, [accessorKey, data, maxNumberOfPieSlicesToDisplay, nameMap, t]);

    if (card) {
        return (
            <DataVisualCard
                filteredResults={filteredResults}
                loading={loading || transforming}
                subtitle={subtitle}
                title={title}
                tooltip={tooltip}
            >
                <Box
                    alignItems="center"
                    data-testid="dataAPIVisualsPieChartWithCard"
                    display="flex"
                    justifyContent="center"
                    width="100%"
                >
                    <PieChart
                        cursor={cursor}
                        data={pieData}
                        isRiskChart={isRiskChart}
                        emptyMessage={emptyMessage || t('Common.noData')}
                        noLegend={noLegend}
                        outerRadius={40}
                        popoverContentsColumns={popoverContentsColumns}
                        responsiveContainerHeight={166} // Magic number that fits with the size of the card
                        showPopover={showPopover}
                    />
                </Box>
            </DataVisualCard>
        );
    }

    return (
        <PieChart
            cursor={cursor}
            data={pieData}
            isRiskChart={isRiskChart}
            emptyMessage={emptyMessage || t('Common.noData')}
            noLegend={noLegend}
            outerRadius={40}
            popoverContentsColumns={popoverContentsColumns}
            responsiveContainerHeight={166} // Magic number that fits with the size of the card
            showPopover={showPopover}
        />
    );
};
